import { Children, createElement, isValidElement } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import { remarkAlert } from 'remark-github-blockquote-alert';
import { MermaidDiagram } from './MermaidDiagram';

/**
 * Flatten children into a plain text string for heading slug generation.
 */
const flatten = (text: string, child: any): string => {
  if (!child) return text;
  return typeof child === 'string'
    ? text + child
    : Children.toArray(child.props.children).reduce(flatten, text);
};

/**
 * Custom heading renderer that generates anchor-friendly slugs.
 * Mirrors the Backstage MarkdownContent heading renderer.
 */
const headingRenderer = ({
  level,
  children,
}: {
  level: number;
  children: React.ReactNode;
}) => {
  const childrenArray = Children.toArray(children);
  const text = childrenArray.reduce(flatten, '');
  const slug = text.toLocaleLowerCase('en-US').replace(/\W/g, '-');
  return createElement(`h${level}`, { id: slug }, children);
};

const isMermaidCodeBlock = (
  child: React.ReactNode,
): child is React.ReactElement<{
  className?: string;
  children?: React.ReactNode;
}> => {
  return (
    isValidElement(child) &&
    typeof child.props.className === 'string' &&
    child.props.className.split(/\s+/).includes('language-mermaid')
  );
};

const preRenderer = ({
  children,
  ...props
}: React.ComponentPropsWithoutRef<'pre'>) => {
  const child = Children.toArray(children).find(isMermaidCodeBlock) ?? null;

  if (isMermaidCodeBlock(child)) {
    const chart = Children.toArray(child.props.children)
      .reduce(flatten, '')
      .replace(/\n$/, '');

    return <MermaidDiagram chart={chart} className="mermaid-diagram" />;
  }

  return <pre {...props}>{children}</pre>;
};

const useStyles = makeStyles(
  theme => ({
    markdown: {
      '& pre': {
        overflowX: 'auto',
      },
      '& table': {
        borderCollapse: 'collapse',
        border: `1px solid ${theme.palette.border}`,
      },
      '& th, & td': {
        border: `1px solid ${theme.palette.border}`,
        padding: theme.spacing(1),
      },
      '& td': {
        wordBreak: 'break-word',
        overflow: 'hidden',
        verticalAlign: 'middle',
        lineHeight: '1',
        margin: 0,
        padding: theme.spacing(3, 2, 3, 2.5),
        borderBottom: 0,
      },
      '& th': {
        backgroundColor: theme.palette.background.paper,
      },
      '& tr': {
        backgroundColor: theme.palette.background.paper,
      },
      '& tr:nth-child(odd)': {
        backgroundColor: theme.palette.background.default,
      },
      '& a': {
        color: theme.palette.link,
      },
      '& img': {
        maxWidth: '100%',
      },
      '& .mermaid-diagram': {
        margin: theme.spacing(2, 0),
        overflowX: 'auto',
      },
      '& .mermaid-diagram svg': {
        display: 'block',
        height: 'auto',
        maxWidth: '100%',
      },
      '& .mermaid-diagram-error': {
        color: theme.palette.error.main,
        marginBottom: theme.spacing(1),
      },
    },
  }),
  { name: 'GfmMarkdownContent' },
);

const components: Record<string, any> = {
  h1: headingRenderer,
  h2: headingRenderer,
  h3: headingRenderer,
  h4: headingRenderer,
  h5: headingRenderer,
  h6: headingRenderer,
  pre: preRenderer,
};

/**
 * Renders an image as a link to it, so nothing loads until the reader
 * clicks. For untrusted content, where an image URL could carry data out.
 */
const imageAsLinkRenderer = ({
  src,
  alt,
}: React.ComponentPropsWithoutRef<'img'>) =>
  src ? (
    <a href={src} target="_blank" rel="noopener noreferrer">
      {alt || src}
    </a>
  ) : (
    <>{alt}</>
  );

const linkedImageComponents: Record<string, any> = {
  ...components,
  img: imageAsLinkRenderer,
};

interface GfmMarkdownContentProps {
  content: string;
  transformImageUri?: (src: string) => string;
  transformLinkUri?: (href: string) => string;
  className?: string;
  /** Show images as links instead of loading them (untrusted content). */
  linkImages?: boolean;
}

/**
 * A GFM markdown renderer that supports GitHub-style alert/callout panels
 * (`> [!NOTE]`, `> [!WARNING]`, etc.) via `remark-github-blockquote-alert`.
 *
 * This replaces `@backstage/core-components` `MarkdownContent` in contexts
 * where alert support is needed — that upstream component does not accept
 * custom remark plugins.
 *
 * Alert panel CSS is in `bui-overrides.css` (section: GFM Alert Panels).
 */
export function GfmMarkdownContent({
  content,
  transformImageUri,
  transformLinkUri,
  className,
  linkImages = false,
}: GfmMarkdownContentProps) {
  const classes = useStyles();

  return (
    <ReactMarkdown
      remarkPlugins={[gfm, remarkAlert]}
      className={`${classes.markdown} ${className ?? ''}`.trim()}
      components={linkImages ? linkedImageComponents : components}
      transformImageUri={transformImageUri}
      transformLinkUri={transformLinkUri}
    >
      {content}
    </ReactMarkdown>
  );
}
